import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../utils/api';

export default function Dashboard() {
  const { user, setPage } = useApp();
  const [stats, setStats]   = useState({ total:0, sent:0, draft:0, ttd_total:0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/stats'), api.get('/quotes')])
      .then(([s, q]) => { setStats(s); setRecent(q.slice(0,8)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = n => isNaN(n)?'0':'TT$'+Number(n).toLocaleString('en-TT',{minimumFractionDigits:0,maximumFractionDigits:0});

  const STATUS = {
    sent:     { bg:'#EAF7F2', color:'#0D6B44', dot:'#1DB373' },
    draft:    { bg:'#F0EDE8', color:'#4A4A52', dot:'#B0B0BA' },
    accepted: { bg:'#EBF4FF', color:'#1A4A80', dot:'#2B6CB0' },
    declined: { bg:'#FEECEC', color:'#B02020', dot:'#E03B3B' },
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="animate-fade-up">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-eyebrow">{new Date().toLocaleDateString('en-TT',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
          <div className="page-title">{greeting}, {user.name.split(' ')[0]}.</div>
          <div className="page-sub">Here's what's happening with your quotes today.</div>
        </div>
        <button className="btn btn-copper" onClick={()=>setPage('newquote')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
          New quotation
        </button>
      </div>

      {/* Stats */}
      <div className="grid-4 stagger" style={{ marginBottom:28 }}>
        {[
          { label:'Total quotes',   value: stats.total,     sub:'All time',          accent:'#0B1F3A' },
          { label:'Sent',           value: stats.sent,      sub:'Delivered to client',accent:'#1DB373' },
          { label:'Drafts',         value: stats.draft,     sub:'Awaiting completion',accent:'#7A7A85' },
          { label:'Total quoted',   value: fmt(stats.ttd_total), sub:'From sent quotes', accent:'#C4621A', mono:true },
        ].map(s => (
          <div key={s.label} className="stat-card animate-fade-up">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color:s.accent, fontFamily:s.mono?'var(--font-mono)':'var(--font-display)', fontSize:s.mono?20:28 }}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Recent quotes */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, letterSpacing:'-0.01em' }}>Recent quotations</div>
        <button className="btn btn-outline btn-sm" onClick={()=>setPage('quotes')}>View all →</button>
      </div>

      <div className="table-wrap animate-fade-up" style={{ animationDelay:'0.15s' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Quote #</th>
              <th>Client</th>
              <th>Service</th>
              <th>Origin</th>
              <th style={{ textAlign:'right' }}>Total TTD</th>
              <th>Status</th>
              {user.role==='admin' && <th>Created by</th>}
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding:40, textAlign:'center' }}>
                <div className="spinner" style={{ margin:'0 auto' }}/>
              </td></tr>
            ) : recent.length===0 ? (
              <tr><td colSpan={8}>
                <div className="empty-state" style={{ padding:48 }}>
                  <div className="empty-state-icon">📋</div>
                  <div className="empty-state-title">No quotes yet</div>
                  <div className="empty-state-sub">Create your first quotation to get started</div>
                </div>
              </td></tr>
            ) : recent.map(q => {
              const sm = STATUS[q.status]||STATUS.draft;
              return (
                <tr key={q.id}>
                  <td>
                    <span style={{ fontFamily:'var(--font-mono)', fontSize:12, fontWeight:600, color:'var(--copper)' }}>{q.quote_number}</span>
                  </td>
                  <td>
                    <div style={{ fontWeight:600 }}>{q.client_name}</div>
                    {q.client_email && <div style={{ fontSize:11, color:'var(--muted)' }}>{q.client_email}</div>}
                  </td>
                  <td style={{ color:'var(--mid)' }}>{q.service}</td>
                  <td style={{ color:'var(--mid)', fontSize:12 }}>{q.origin}</td>
                  <td style={{ textAlign:'right', fontFamily:'var(--font-mono)', fontSize:12, fontWeight:600 }}>
                    {q.total_ttd ? 'TT$'+Number(q.total_ttd).toLocaleString('en-TT',{minimumFractionDigits:2,maximumFractionDigits:2}) : '—'}
                  </td>
                  <td>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:99, background:sm.bg, color:sm.color, fontSize:11, fontWeight:600 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:sm.dot, display:'inline-block' }}/>
                      {q.status}
                    </span>
                  </td>
                  {user.role==='admin' && <td style={{ fontSize:12, color:'var(--muted)' }}>{q.created_by_name}</td>}
                  <td style={{ fontSize:12, color:'var(--muted)' }}>{new Date(q.created_at).toLocaleDateString('en-TT',{day:'2-digit',month:'short',year:'numeric'})}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {recent.length > 0 && <div className="table-footer">{recent.length} recent quote{recent.length!==1?'s':''} shown</div>}
      </div>

      {/* Quick actions */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginTop:24 }}>
        {[
          { label:'New quote',     sub:'Start a fresh quotation',  icon:'✦', action:'newquote', color:'var(--copper)' },
          { label:'View clients',  sub:'Manage your client list',   icon:'◈', action:'clients',  color:'#2B6CB0' },
          { label:'All quotes',    sub:'Browse quote history',      icon:'◉', action:'quotes',   color:'#1DB373' },
        ].map(a => (
          <button key={a.label} onClick={()=>setPage(a.action)}
            style={{ background:'var(--white)', border:'1px solid rgba(0,0,0,0.06)', borderRadius:14, padding:'18px 20px', cursor:'pointer', textAlign:'left', transition:'all 0.2s var(--ease-out)', boxShadow:'var(--shadow-xs)' }}
            onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
            onMouseLeave={e=>{ e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='var(--shadow-xs)'; }}>
            <div style={{ fontSize:22, marginBottom:8 }}><span style={{ color:a.color }}>{a.icon}</span></div>
            <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{a.label}</div>
            <div style={{ fontSize:12, color:'var(--muted)' }}>{a.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
